import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';

import {
    Toolbar, Button, Box, Avatar, IconButton, Tooltip, Fade, Chip
} from '@mui/material/';

import {useRecoilState} from 'recoil';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

import {
    errorState, userState, quotesState, openOrdersState, shippedOrdersState, currentQuoteState
} from "../state/state";
import {useNavigate} from "react-router-dom";
import {useEffect} from 'react';
import {initQuote} from "../types.d";

// Import Tempco UI components
import {
    TempcoButton,
    TempcoHeading,
    TempcoBodyText,
    TempcoGradientText,
    TempcoStatusChip,
    tempcoColors
} from './TempcoUI';

function NavAppBar() {
    const user = useRecoilState(userState)[0];
    const navigate = useNavigate();
    const [error, setError] = useRecoilState(errorState);
    const [quotes, setQuotes] = useRecoilState(quotesState);
    const [openOrders, setOpenOrders] = useRecoilState(openOrdersState);
    const [shippedOrders, setShippedOrders] = useRecoilState(shippedOrdersState);
    const [currentQuote, setCurrentQuote] = useRecoilState(currentQuoteState);

    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    // useEffect - init
    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    useEffect(() => {
        return () => {
            // this now gets called when the component unmounts
        };
    }, []);

    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    // handleExit
    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    const handleExit = () => {
        sessionStorage.removeItem('user');
        let newError = {...error, isError: false}
        setError(newError);
        
        // Clear all table data when logging out
        setQuotes([]);
        setOpenOrders([]);
        setShippedOrders([]);
        setCurrentQuote(initQuote());
        
        navigate("/");
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    // handleSettings
    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    const handleSettings = () => {
        navigate("/Update");
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    // handleHome
    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    const handleHome = () => {
        navigate("/Home");
    };

    if (!user?.isLoggedIn) return null;

    return (
        <Fade in={true} timeout={500}>
            <AppBar
                position="static"
                elevation={0}
                className="tempco-nav"
                sx={{
                    background: 'rgba(51, 51, 51, 0.95)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: `1px solid rgba(237, 28, 36, 0.2)`,
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    boxShadow: `0 4px 20px rgba(237, 28, 36, 0.15)`,
                    width: '100vw',
                    marginLeft: 0,
                    marginRight: 0,
                    left: 0,
                    right: 0,
                }}
            >
                <Toolbar disableGutters sx={{minHeight: 80, px: 3}}>
                    {/* Enhanced Logo Section with Tempco Branding */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            padding: '4px 8px',
                            borderRadius: 2,
                            background: 'rgba(237, 28, 36, 0.05)',
                            border: `1px solid rgba(237, 28, 36, 0.1)`,
                            '&:hover': {
                                transform: 'scale(1.02)',
                                background: 'rgba(237, 28, 36, 0.1)',
                                borderColor: 'rgba(237, 28, 36, 0.2)',
                                boxShadow: `0 4px 12px rgba(237, 28, 36, 0.2)`,
                            }
                        }}
                        onClick={handleHome}
                    >
                        <img
                            className="navbar-brand"
                            src={process.env.PUBLIC_URL + '/Tempco.png'}
                            alt='Tempco Electric Heater Corporation'
                            height='50px'
                            style={{
                                paddingRight: 12,
                                filter: 'brightness(1.1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                            }}
                        />
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <TempcoBodyText variant="body2" sx={{ 
                                fontSize: '0.75rem', 
                                opacity: 0.8,
                                lineHeight: 1.2
                            }}>
                                Electric Heater Corporation
                            </TempcoBodyText>
                        </Box>
                    </Box>

                    {/* Enhanced Title Section with Tempco Gradient */}
                    <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center', ml: 3}}>
                        <Box>
                            <TempcoGradientText
                                variant="h5"
                                component="h1"
                                sx={{
                                    fontFamily: '"Oswald", "Arial Black", "Arial", sans-serif',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                    lineHeight: 1.2,
                                }}
                            >
                                Order Tracker Web
                            </TempcoGradientText>
                        </Box>
                    </Box>

                    {/* Enhanced User Info Section with Tempco Styling */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Box 
                            sx={{
                                display: 'flex', 
                                alignItems: 'center', 
                                background: 'rgba(237, 28, 36, 0.05)',
                                border: `1px solid rgba(237, 28, 36, 0.1)`,
                                borderRadius: 2,
                                padding: '8px 12px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: 'rgba(237, 28, 36, 0.1)',
                                    borderColor: 'rgba(237, 28, 36, 0.2)',
                                }
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: tempcoColors.primary,
                                    width: 36,
                                    height: 36,
                                    mr: 1.5,
                                    background: `linear-gradient(45deg, ${tempcoColors.primary} 30%, ${tempcoColors.light} 90%)`,
                                    boxShadow: `0 4px 12px rgba(237, 28, 36, 0.4)`,
                                    border: `2px solid rgba(255, 255, 255, 0.1)`,
                                }}
                            >
                                <PersonIcon fontSize="small"/>
                            </Avatar>
                            <Box sx={{display: {xs: 'none', sm: 'block'}}}>
                                <TempcoBodyText variant="body2" sx={{
                                    color: tempcoColors.white, 
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                }}>
                                    {user.username || 'User'}
                                </TempcoBodyText>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    {user.isAdmin ? (
                                        <TempcoStatusChip 
                                            status="complete" 
                                            label="Administrator"
                                        />
                                    ) : (
                                        <TempcoStatusChip 
                                            status="active" 
                                            label="User"
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Enhanced Action Buttons with Tempco Styling */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, ml: 2}}>
                        <Tooltip title="Dashboard Home" arrow>
                            <IconButton
                                onClick={handleHome}
                                className="tempco-nav-item"
                                sx={{
                                    color: tempcoColors.white,
                                    background: 'rgba(237, 28, 36, 0.1)',
                                    borderRadius: 2,
                                    border: `1px solid rgba(237, 28, 36, 0.2)`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: 'rgba(237, 28, 36, 0.2)',
                                        borderColor: 'rgba(237, 28, 36, 0.4)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px rgba(237, 28, 36, 0.3)`,
                                        color: tempcoColors.light,
                                    },
                                }}
                            >
                                <HomeIcon/>
                            </IconButton>
                        </Tooltip>

                        {user?.isAdmin && (
                            <Tooltip title="User Administration" arrow>
                                <TempcoButton
                                    variant="outlined"
                                    startIcon={<AdminPanelSettingsIcon/>}
                                    onClick={handleSettings}
                                    sx={{
                                        color: tempcoColors.white,
                                        borderColor: tempcoColors.primary,
                                        background: 'rgba(237, 28, 36, 0.05)',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2,
                                        fontSize: '0.875rem',
                                        minHeight: '40px',
                                        '&:hover': {
                                            borderColor: tempcoColors.primary,
                                            background: 'rgba(237, 28, 36, 0.1)',
                                            color: tempcoColors.light,
                                        },
                                    }}
                                >
                                    Admin
                                </TempcoButton>
                            </Tooltip>
                        )}

                        <Tooltip title="Secure Logout" arrow>
                            <Button
                                variant="contained"
                                startIcon={<LogoutIcon/>}
                                onClick={handleExit}
                                sx={{
                                    background: `linear-gradient(45deg, ${tempcoColors.secondary} 30%, ${tempcoColors.primary} 90%)`,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    minHeight: '40px',
                                    boxShadow: `0 4px 12px rgba(173, 18, 25, 0.4)`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: `linear-gradient(45deg, #8B0F14 30%, ${tempcoColors.secondary} 90%)`,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 6px 16px rgba(173, 18, 25, 0.6)`,
                                    },
                                }}
                            >
                                Logout
                            </Button>
                        </Tooltip>
                    </Box>
                </Toolbar>

                {/* Brand Strip */}
                <Box
                    sx={{
                        background: `linear-gradient(90deg, ${tempcoColors.primary} 0%, ${tempcoColors.light} 50%, ${tempcoColors.primary} 100%)`,
                        height: 3,
                        width: '100%',
                        opacity: 0.8,
                    }}
                />
            </AppBar>
        </Fade>
    );
}

export {NavAppBar};